import './App.css';

import logo from './Images/logo_carvingtracer.png';
import altar_plate from './Images/altar_plate.png';
import scanning from './Images/scanning.png';
import bvh from './Images/bvh.png';
import circumcircle from './Images/circumcircle.png';
import construction from './Images/construction.png';
import face_color from './Images/face_color.png';
import mesh from './Images/mesh.png';
import raycasting from './Images/raycasting.png';
import react_logo from './Images/logo.svg';
import three_logo from './Images/three_cropped.png';
import brushes from './Images/brush_size_color.png';
import toolbar from './Images/toolbar.png';
import uncertaintyTracing from './Images/uncertaintyTracing.png';
import MSIITracing from './Images/MSIITracing.png';

function Home() {
  return (
    <div className="Home">
      <h1 style={{paddingLeft: "30px", paddingTop: "40px", backgroundColor: "black"}}>
        CarvingTracer
      </h1>
      <h2 style={{paddingLeft: "30px", paddingTop: "20px", paddingBottom: "20px"}}>
        An Annotation Framework for Paleographers Tracing 3D Inscriptions
      </h2>
      <h3 style={{paddingLeft: "30px", paddingBottom: "20px"}}>
        Advanced Practical by Antonia Schlieder supervised by Dr. Susanne Krömker
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      {"In paleography, it is common to create tracings of writings, either to present findings or discuss interpretations. There are established tools for digital drawing that can be used for tracing letters as well. However, such solutions are focused on drawing in two dimensions. In the case of inscriptions, such as stone carvings, a tool that draws in 3D becomes necessary. This project presents the CarvingTracer, a web-based annotation framework for tracing inscriptions in 3D."}
      </p>
      <img src={logo} className="App-logo" alt="logo" style={{paddingLeft: "40%", paddingTop: "10px", paddingBottom: "10px"}}/>
      <div style={{display: "flex", justifyContent: "space-evenly"}}>
        <a style={{color: "white"}} href="https://pille.iwr.uni-heidelberg.de/~nametool01/carvingtracer/">CarvingTracer</a>
        <a style={{color: "white"}} href="https://youtu.be/on6-kiBWsxM">Tutorial</a> 
      </div>     
      <h3 style={{paddingLeft: "30px", paddingTop: "20px", paddingBottom: "20px"}}>
        Motivation
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
        Paleographers come across all kinds of materials when researching inscriptions. 
        Commonly, writing is analyzed in two dimensions. In the case of carved or impressed letters 
        (e.g., stone carvings and cuneiform tablets), each letter stroke also has a depth to it. 
        Hence, there are three dimensions to consider when deciphering such writings. 
        Automatic segmentation of cuneiform in 3D has been shown to work well. 
        However, carvings in a material with a rough surface, such as sandstone, 
        pose a challenge for automatic script segmentation. The graininess of the stone 
        interferes with the carved inscriptions. Since a fully automatic approach is 
        not possible, a tool that allows domain experts to manually annotate writing is necessary. 
        While there is a variety of 3D editing tools available, most of them are over-the-top 
        for laymen (e.g., Blender) or do not offer the functionality needed when tracing inscriptions (e.g., MeshLab).
        <br/>
        There are several application scenarios for the CarvingTracer: it can aid communication between 
        researchers and provide access to resources that are not easily available.
        It can also function as a tool to utilize crowd wisdom because annotations can be
        easily compared and aggregated. Later on, annotations created with this tool could be
        analyzed systematically or be used as training data for learning-based approaches to
        script segmentation. As examples for carved inscriptions we use 3D scans of the altar
        plate in the church of St. Peter and Paul, Niederzell that were collected in 2021.
        <br/>
        On this page, users can find the requirements of the project, an explanation of algorithmic details,
        information on the implementation and the libraries used as well as a short tutorial on the features
        of the CarvingTracer. 
        Those who want to get started with tracing immediately, can follow the link above to try out the CarvingTracer
        on one of the carved signatures on the altar plate. The tutorial link takes you to a short video demonstrating
        the CarvingTracer features.
      </p>
      <h3 style={{paddingLeft: "30px", paddingTop: "80px", paddingBottom: "20px"}}>
        Tracing by Hand and Computer-Assisted Documentation of Inscriptions
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
        The paleographer has to read characters from a writing medium that has been deteriorated 
        by aging processes. The depth dimension of the characters provides valuable
        information for this task. As a consequence, written monuments are increasingly
        being documented using 3D scans. To put it differently, even if the characters of
        writing systems such as the Latin alphabet are composed of linear features (strokes),
        characters in clay, wax or stone are not a purely 2D phenomenon.
        <br/>
        A writing material with a number of special features are clay tablets, which were used
        for the various cuneiform scripts. Forms of 3D documentation have been explored here
        for some time, with computer science research focusing on the algorithmic recognition
        of the characters. There are two reasons why this approach is not feasible in the case of the seemingly
        simpler inscriptions from the island of Reichenau. Firstly, the stone tablets are affected
        by aging processes that produce a much rougher surface than that of the much
        older clay tablets. This considerably impairs the legibility of the characters. The
        second reason lies in a requirement of the paleographers. They would like to trace
        the comparatively few but difficult-to-read signs themselves and thus document the
        interpretation process that the transcription represents in a transparent manner.
      </p>
      <figure style={{display: "flex", justifyContent: "space-evenly", paddingTop: "40px"}}>
        <figure style={{paddingBottom: "10px", textAlign: "center"}}>
          <img src={scanning} alt="Scanning" style={{height: "30vmin"}}/>
          <figcaption>3D scanning process.</figcaption>
        </figure> 
        <figure style={{paddingBottom: "10px", textAlign: "center"}}>
          <img src={altar_plate} alt="Altar Plate" style={{height: "30vmin"}}/>
          <figcaption>3D scan of part of the altar plate.</figcaption>
        </figure>
      </figure>
      <h3 style={{paddingLeft: "30px", paddingTop: "5vh", paddingBottom: "20px"}}>
        Requirements Analysis
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
        We conducted an informal interview with a domain expert, a paleographer, to identify expert needs. 
        In addition, we had several sessions where the
        expert tried out other 3D viewing and 3D drawing software, such as Meshlab, to gain a
        better understanding of a realistic user scenario.
        The design requirements derived from these sessions either refer to 
        interactions for tracing or metadata of the annotations. Tracing in 3D should mostly work
        like drawing in 2D with the possibility to make corrections. Users should be able to
        express uncertainty in their tracings which is encoded for each drawn stroke and stored
        in the annotation metadata along with interpretations.
        <ul style={{paddingLeft: "30px", paddingTop: "10px", paddingBottom: "10px"}}>
          <li>Undo stack: store drawing history to undo and redo strokes as wanted</li>
          <li>Eraser tool: low-level correction of strokes</li>
          <li>Annotation metadata: encode tracing uncertainty and interpretation of inscription</li>
        </ul>
        Along with domain-specific requirements there are technical expectations for the
        framework that ensure a good user experience.
        <ul style={{paddingLeft: "30px", paddingTop: "10px"}}>
          <li>Accessible from any machine: browser-based implementation</li>
          <li>Light-weight and fast: few dependencies on other libraries</li>
          <li>Easy to use: controls follow conventions and work for different setups (e.g., touchscreen and stylus)</li>
        </ul>
      </p>
      <h3 style={{paddingLeft: "30px", paddingTop: "50px", paddingBottom: "20px"}}>
        Raycasting for Drawing in 3D
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      3D scans are commonly stored in polygon file format (PLY). A PLY file encodes
      geometry and color information on indexed vertices. This allows us to
      render the 3D scan as a triangle mesh. The three vertices that span a face determine
      its color. A user who draws a line on the triangle mesh, specifies a
      sequence of vertices. As the user interacts with a pointing device in screen space, the
      screen coordinates have to be matched to a vertex.
      </p>
      <figure style={{display: "flex", justifyContent: "space-evenly", paddingTop: "50px"}}>
        <figure style={{paddingBottom: "20px", textAlign: "center"}}>
          <img src={mesh} alt="Triangle mesh" style={{height: "30vmin"}}/>
          <figcaption>Zoomed-in triangle mesh of 3D scan.</figcaption>
        </figure> 
        <figure style={{paddingBottom: "20px", textAlign: "center"}}>
          <img src={face_color} alt="Mesh colors" style={{height: "30vmin"}}/>
          <figcaption>Vertex colors determine face colors of mesh.</figcaption>
        </figure>
      </figure>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      To draw on the surface of a 3D object we have to determine which vertices of the
      object will be affected. The CarvingTracer employs ray casting to find these vertices.
      While ray casting only makes for a simple scene rendering algorithm (ie. no light
      reflections), it is well-suited for quickly determining which vertices of the object will be
      drawn on. The ray casting algorithm performs a ray-to-triangle intersection test for
      each face of each object in the scene and returns the triangles that are intersected first.
      A naive algorithm for finding the intersecting triangle simply tests all
      triangles which the computational cost to increase linearly with the number of triangles.
      </p>
      <figure style={{display: "flex", justifyContent: "space-evenly", paddingTop: "50px"}}>
        <figure style={{paddingBottom: "20px", textAlign: "center"}}>
          <img src={raycasting} alt="Scanning" style={{height: "30vmin"}}/>
          <figcaption>Raycasting for intersection tests.</figcaption>
        </figure> 
        <figure style={{paddingBottom: "20px", textAlign: "center"}}>
          <img src={circumcircle} alt="Altar Plate" style={{height: "30vmin", fillOpacity: "100%"}}/>
          <figcaption>Smallest circumcircle of triangle in mesh determines ray size.</figcaption>
        </figure>
      </figure>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      It is important to note that the CarvingTracer does not draw on a mesh but on a point
      cloud. This is for technical as well as conceptual reasons: 3D objects have colors
      assigned to their vertices but not their faces. Computationally, this means we model
      each point or vertex as a degenerate triangle (the three vertices coincide) before we
      perform the ray casting. Since the ray and vertices are both infinitely small, hitting a
      vertex is highly unlikely. Most ray casting libraries (including THREE.js used by the
      CarvingTracer ) offer an option to define buffers for the vertices and for the ray. If we
      increase the radius of the ray to be at least the size of the largest circumcircle of a
      triangle in the mesh, we can ensure that a ray hitting the triangle hits at least one of
      the triangle's vertices. In the implementation, the ray is cast at the
      cursor position. Another issue solved by employing ray casting are situations in which
      the surface of the object (partially) occludes itself. The object vertices that are hit first
      change color. When the cursor moves the ray changes, target and new intersections
      tests are performed. This way, we can use the cursor to draw on a 3D object.
      </p>
      <h3 style={{paddingLeft: "30px", paddingTop: "50px", paddingBottom: "20px"}}>
        Bounding Volume Hierarchy
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      For geometric shapes that can be described with few vertices, a basic ray casting
      implementation works well. 3D scans of real-life objects, however, can easily have
      hundreds of thousands of vertices. The 3D scan of the carved inscription in Fig. 4.3
      consists of 173,422 vertices for example. At the same time we perform many con-
      secutive intersection tests during drawing because the target of the cast ray moves.
      Therefore performance improvements are needed to ensure a smooth experience for
      the user.
      <br/>
      A classical approach in computer graphics to speed up the indexing of geometric
      objects consists in generating a volume hierarchy (BVH). This approach
      reduces the worst case performance of ray casting from linear to logarithmic runtime
      depending on the number of triangles. The idea behind BVH is to simplify the 3D scene and
      determine the general area that the ray will hit first. The scene is split up into bounded
      volumes that become smaller further down the hierarchy (see Fig. 4.2(b)). At leaf level
      the volumes consist of a bounded number of triangles.
      <br/>
      The choice of bounding volume is important. While complex shapes can fit objects
      better, intersection tests are also more complex. Commonly, axis-aligned bounding
      boxes (AABBs) are used since they require little memory to be stored and are easy
      to test for intersections. For 3D scans of (stone) surfaces this works particularly well,
      since AABBs fit tightly around the cuboid surfaces and their subdivisions.
      </p>
      <figure style={{display: "flex", justifyContent: "space-evenly", paddingTop: "50px"}}>
        <figure style={{paddingBottom: "20px", textAlign: "center"}}>
          <img src={bvh} alt="bounding volume hierarchy" style={{height: "30vmin"}}/>
          <figcaption>Bounding volume hierarchy.</figcaption>
        </figure> 
        <figure style={{paddingBottom: "20px", textAlign: "center"}}>
          <img src={construction} alt="construction of bvh" style={{height: "20vmin"}}/>
          <figcaption>Construction of BVH for one of our examples.</figcaption>
        </figure>
      </figure>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      There are two parameters of the BVH that need to be decided before construction:
      tree depth versus degree (number of children in a node). Higher depth increases
      construction and tree-traversal time, but decreases time spent at each node . The
      BVH implementation used by the CarvingTracer builds a binary search tree
      since these are easy to construct. Fig. 4.3 shows levels of the constructed hierarchy
      for our example carving. The BVH aims to be balanced in terms of tree structure as
      well as bounding volume content. Nodes that are close in the hierarchy should be
      physically close as well, with minimal overlap between their bounded volumes.
      <br/>
      While the degree of the BVH is given, the maximum depth of the tree (maxDepth) and
      the maximum number of triangles in a leaf (maxLeafTriangles) have to be chosen.
      A larger value of maxLeafTriangles affects the size of the bounded
      volumes at lower levels. A higher maximum depth as well as a lower maximum number 
      of triangles in leafs increase construction time of the BVH and working memory used. 
      On the other hand this parameter combination leads to the best ray casting performance.
      For our framework we want to render a scene with a single 3D object with static
      geometry, which means the BVH needs to be computed only once. At the same time
      we want to ensure a smooth user experience while drawing. Therefore we opt to
      optimize for best ray casting performance at the cost of better construction time.
      </p>
      <h3 style={{paddingLeft: "30px", paddingTop: "50px", paddingBottom: "20px"}}>
        Framework and Tools
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      The implementation of the CarvingTracer uses the JavaScript 3D rendering
      library, THREE.js. A key advantage of THREE.js, besides its compactness and
      compatibility with all common browsers, is its great performance when producing
      GPU-based 3D animations. The tracing component is embedded in a React.js app, which allows easy deployment and 
      extensions of the framework. To improve raycasting performance we adapted the bounding 
      volume hierarchy implementation by <a href="https://github.com/gkjohnson/three-mesh-bvh">Garrett Johnson</a>.
      </p>
      <figure style={{display: "flex", justifyContent: "space-evenly", paddingTop: "20px"}}>
        <figure style={{paddingBottom: "10px", textAlign: "center"}}>
          <img src={react_logo} alt="React logo" style={{height: "20vmin"}}/>
        </figure> 
        <figure style={{paddingBottom: "10px", textAlign: "center"}}>
          <img src={three_logo} alt="Three.js logo" style={{height: "20vmin"}}/>
        </figure>
      </figure>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}> 
        During development we compared two different implementations of the drawing tool.
        Initially, the 3D scan of the inscription was rendered as a triangle mesh object. The
        user would draw directly on the mesh which overwrote the original vertex colors. When
        adding undo and eraser functionality, it became necessary to store the original vertex
        colors as an object with the same vertex indexing. This means we have to store two
        identical 3D objects for one 3D scan. In THREE.js, drawing directly on the mesh also
        leads to issues if the tracing is supposed to be transparent, i.e. the original texture of
        the scan still shines through. Because of this we decided to instead create a point
        cloud of the 3D scan for drawing and a mesh of the 3D scan for storing and showing
        the original vertex colors. The transparent point cloud initially has alpha channel
        values of 0 for all vertices and is rendered on top of the mesh with the original
        vertex colors. When drawing on the point cloud, the color values as well as the alpha
        channel values of the affected vertices change. This simplifies the undo stack, solves
        any issues with transparency and makes exporting the annotated object with different
        options easier. By excluding the background object from the ray casting intersection
        tests performance remains the same.
        <br/>
        While THREE.js offers many useful features, we had to adapt to certain limitations.
        The CarvingTracer uses custom shaders because the built-in shaders of THREE.js 
        do not support shading and vertex colors with an alpha
        channel for point clouds. Shaders in THREE.js are written in GLSL and run on the
        GPU. There are two types: vertex shaders and fragment shaders. The vertex shader
        runs first and determines vertex positions and sizes. It can pass additional attributes to
        the fragment shader. Vertex colors are computed by the fragment shader. In our case
        the vertex shader changes vertex size depending on its distance to the view point. 
        Our fragment shader computes Lambertian reflection to determine
        each vertex color and then adds a dimension to the color vector for the alpha channel.
      </p>
      <h3 style={{paddingLeft: "30px", paddingTop: "50px", paddingBottom: "20px"}}>
        User Interface
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      User interactions of the CarvingTracer follow established conventions of 3D viewing
      and editing tools: left-click rotates the object, right-click moves it and with the mouse
      wheel the user zooms in and out of the scene. When holding down the shift key,
      drawing mode is activated and orbit controls are disabled. With left-clicking, the user
      can then draw in strokes on the object. A video tutorial explains the
      controls in detail.
      <br/>
      The user can choose between different brush sizes, which is implemented through
      smaller and larger ray casting radii. Different brush colors correspond to different levels
      of tracing certainty. In case of mistakes, the user can either undo/redo
      full strokes or correct their tracing with the eraser tool. Undo/redo functionality uses a
      stack structure that contains drawn strokes. Each stroke is described by a list of vertex
      indices and colors. Users can switch between three different light directions by repeatedly
      clicking the ''light'' button in the toolbar.
      </p>
      <figure style={{display: "flex", justifyContent: "space-evenly", paddingTop: "50px"}}>
        <figure style={{paddingBottom: "20px", textAlign: "center"}}>
          <img src={brushes} alt="Different brush sizes and colors." style={{height: "30vmin"}}/>
          <figcaption>Different brush sizes and colors.</figcaption>
        </figure> 
        <figure style={{paddingTop: "60px", paddingBottom: "20px", textAlign: "center"}}>
          <img src={toolbar} alt="toolbar" style={{height: "10vmin"}}/>
          <figcaption>The toolbar of the CarvingTracer.</figcaption>
        </figure>
      </figure>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      The user can export the annotated object in PLY format. There are two options
      to choose from that affect the texture of the exported object. By default the object
      keeps its original texture and the tracing has the color of the annotated uncertainty
      (uncertaintyTracing export). If multi-scale integral invariant (MSII) filtering of the 3D
      scan is available, the user can also choose to store the MSII colors for the tracings
      and the original texture everywhere else. We refer to this as the MSIITracing export.
      </p>
      <figure style={{display: "flex", justifyContent: "space-evenly", paddingTop: "50px"}}>
        <figure style={{paddingBottom: "20px", textAlign: "center"}}>
          <img src={uncertaintyTracing} alt="Uncerctainty Tracing" style={{height: "30vmin"}}/>
          <figcaption>Example of UncertaintyTracing export.</figcaption>
        </figure> 
        <figure style={{paddingBottom: "20px", textAlign: "center"}}>
          <img src={MSIITracing} alt="MSII Tracing" style={{height: "30vmin"}}/>
          <figcaption>Example of MSIITracing export.</figcaption>
        </figure>
      </figure>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      Additionally, the user can export annotation metadata as a JSON file. Metadata includes
      author name, carving interpretation, ratio of traced vertices to total number of vertices
      in the mesh, ratios of the different certainty levels, and the indices of the mesh vertices
      that are part of the tracing. The certainty distribution of the tracing
      offers insight into and allows to make a high-level comparison of several tracings of
      the same 3D scan.
      </p>
      <h3 style={{paddingLeft: "30px", paddingTop: "50px", paddingBottom: "20px"}}>
        Design Validation and Conclusion
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap"}}>
      Methods from computer graphics have been used in several ways to support paleogra-
      phy research. For carved inscriptions it is possible to document them not only with 2D
      photography, but also with 3D scanning. This way, carving depth can be considered
      when tracing and interpreting inscriptions. While there exist tools to view 3D scans,
      the CarvingTracer offers paleographers a compact, easy-to-use framework to trace
      inscriptions in 3D and export their annotations. As part of this project we conducted
      expert interviews to identify desired features of the annotation tool. Combined with
      technical expectations this gave us a list of design requirements. We have shown how
      the CarvingTracer fulfills them. In regards to implementation, we have optimized ray
      casting performance using a bounding volume hierarchy and tested which parameter
      sets work best for our use case. From the exported annotation the user can access
      tracing information, such as uncertainty, visually as well as in the metadata.
      The current implementation uses no database, hence file uploading and user man-
      agement is not available yet. The user can also only annotate one object at a time. A
      workaround to this could be adding page-routing and creating a new page for each
      object to be annotated. Once a user finishes a tracing, they can move on to the next
      one.
      <br/>
      Further iterations of the CarvingTracer could implement a multi-user interface to further
      facilitate communication between researchers. In the future, we want to collect
      crowd-sourced annotations with the CarvingTracer and conduct a similarity analysis of 3D tracings.
      </p>
      <h3 style={{paddingLeft: "30px", paddingTop: "50px", paddingBottom: "20px"}}>
        References
      </h3>
      <p style={{paddingLeft: "30px", paddingRight: "30px", whiteSpace: "pre-wrap", backgroundColor: "black", paddingBottom: "50px"}}>
      <ul style={{paddingLeft: "30px", paddingTop: "10px", paddingBottom: "10px"}}>
        <li>Bartosz Ryszard Bogacz. “Analyzing handwritten and transcribed symbols in disparate corpora”. PhD thesis. 2018.</li>
        <li>Svenja Bonmann et al. “A partial decipherment of the unknown Kushan script”. In: Transactions of the Philological Society 121.2 (2023), pp. 293-329.</li>
        <li>Paolo Cignoni et al. “Meshlab: an open-source mesh processing tool.” In: Eurographics Italian chapter conference. Vol. 2008. Salerno, Italy. 2008, pp. 129-136.</li>
        <li>Dieter Geuenich, Renate Neumüllers-Klauser, and Karl Schmid. Die Altarplatte von Reichenau-Niederzell. 1983.</li>
        <li>Susanne Krömker. 3D-Scannen von Inschriften einer Mittelalterlichen Altarplatte in der Kirche St. Peter und Paul in Niederzell. https://vngg.iwr.uni-heidelberg.de/Niederzell/. Retrieved on: 11.02.2024.</li>
        <li>Hubert Mara et al. “GigaMesh and gilgamesh - 3D multiscale integral invariant cuneiform character extraction”. In: Proceedings of the 11th International conference on Virtual Reality, Archaeology and Cultural Heritage. 2010, pp. 131-138.</li>
        <li>Hubert Mara. “Multi-scale integral invariants for robust character extraction from irregular polygon mesh data”. PhD thesis. 2012.</li>
        <li>Hubert Mara and Susanne Krömker. “Vectorization of 3D-characters by integral invariant filtering of high-resolution triangular meshes”. In: 2013 12th International Conference on Document Analysis and Recognition. IEEE. 2013, pp. 62-66.</li>
        <li>Philip Schneider and David H Eberly. Geometric tools for computer graphics. Elsevier, 2002.</li>
      </ul>
      </p>
    </div>
  );
}

export default Home;
